// Admin review-tracking governance (Ring-3). Per-store enable/disable toggle
// grouped internal (BO1) vs external sellers + the manual sync trigger (with live
// progress, shared with the reviews screen).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToastStore } from '../../lib/toast'
import { useT } from '../../i18n'
import { reviewsI18n } from '../../i18n/reviews'
import {
  fetchTrackingSettings,
  toggleAdminTracking,
} from '../../api/reviews'
import type { TrackingSetting } from '../../api/reviews.types'
import { ReviewSyncControl } from '../reviews/ReviewSyncControl'

const QUERY_KEY = ['admin', 'review-tracking-settings'] as const

export function ReviewTrackingSettings() {
  const t = useT(reviewsI18n)
  const qc = useQueryClient()
  const pushToast = useToastStore((s) => s.push)

  const settings = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => fetchTrackingSettings(),
  })

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      toggleAdminTracking(id, enabled),
    onSuccess: () => void qc.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (e) =>
      pushToast({ variant: 'error', title: String(e) }),
  })

  const items = settings.data?.items ?? []
  const internal = items.filter((s) => s.is_internal)
  const external = items.filter((s) => !s.is_internal)

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">{t.trackingTitle}</h1>
          <p className="mt-0.5 text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{t.tracking.intro}</p>
        </div>
        <ReviewSyncControl scope="admin" />
      </header>

      {settings.isLoading ? (
        <p className="text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{t.loading}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{t.tracking.noStores}</p>
      ) : (
        <>
          <Group
            title={t.tracking.internalGroup}
            rows={internal}
            onToggle={(id, enabled) => toggle.mutate({ id, enabled })}
            pending={toggle.isPending}
          />
          <Group
            title={t.tracking.externalGroup}
            rows={external}
            onToggle={(id, enabled) => toggle.mutate({ id, enabled })}
            pending={toggle.isPending}
          />
        </>
      )}
    </div>
  )
}

function Group({
  title,
  rows,
  onToggle,
  pending,
}: {
  title: string
  rows: TrackingSetting[]
  onToggle: (id: string, enabled: boolean) => void
  pending: boolean
}) {
  const t = useT(reviewsI18n)
  if (rows.length === 0) return null
  return (
    <section className="rounded-lg border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)]">
      <header className="border-b border-slate-100 appearance-dark:border-[var(--color-border)] px-4 py-2.5">
        <h2 className="text-sm font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">{title}</h2>
      </header>
      <ul className="divide-y divide-slate-100 appearance-dark:divide-[var(--color-border)]">
        {rows.map((s) => (
          <li
            key={s.store_integration_id}
            className="flex items-center justify-between px-4 py-3"
          >
            <div>
              <p className="text-sm font-medium text-slate-900 appearance-dark:text-[var(--color-text)]">{s.shop_name}</p>
              <p className="mt-0.5 text-xs capitalize text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
                {s.provider}
                {s.org_name ? ` · ${s.org_name}` : ''}
              </p>
            </div>
            <button
              type="button"
              disabled={pending}
              onClick={() =>
                onToggle(s.store_integration_id, !s.review_tracking_enabled)
              }
              className={
                'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ring-1 transition disabled:opacity-50 ' +
                (s.review_tracking_enabled
                  ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100'
                  : 'bg-slate-100 appearance-dark:bg-[var(--color-surface-muted)] text-slate-600 appearance-dark:text-[var(--color-text-muted)] ring-slate-200 appearance-dark:ring-[var(--color-border)] hover:bg-slate-200 appearance-dark:hover:bg-[var(--color-surface-muted)]')
              }
            >
              {s.review_tracking_enabled ? t.tracking.enabled : t.tracking.disabled}
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
